/**
 * FolderTreeSelect - Tree dropdown for selecting a folder to quick sync
 */
import { useState, useEffect, useRef } from 'react'
import { HiOutlineFolder, HiOutlineFolderOpen, HiOutlineChevronRight, HiOutlineChevronDown } from 'react-icons/hi'
import clsx from 'clsx'

/**
 * Recursive tree item component
 */
function TreeItem({ folder, selectedId, onSelect, level = 0 }) {
  const [expanded, setExpanded] = useState(false)
  const hasChildren = folder.children && folder.children.length > 0
  const isSelected = folder.drive_id === selectedId

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors rounded-lg',
          isSelected
            ? 'bg-primary-100 text-primary-700'
            : 'hover:bg-gray-100 text-gray-700'
        )}
        style={{ paddingLeft: `${12 + level * 20}px` }}
        onClick={() => onSelect(folder)}
      >
        {/* Expand/collapse button for folders with children */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {expanded ? (
              <HiOutlineChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <HiOutlineChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>
        ) : (
          <span className="w-5" /> // Spacer for alignment
        )}

        {/* Folder icon */}
        {expanded || isSelected ? (
          <HiOutlineFolderOpen className="w-5 h-5 text-amber-500 flex-shrink-0" />
        ) : (
          <HiOutlineFolder className="w-5 h-5 text-amber-500 flex-shrink-0" />
        )}

        {/* Folder name */}
        <span className={clsx('truncate text-sm', isSelected && 'font-medium')}>
          {folder.name}
        </span>
      </div>

      {/* Children */}
      {hasChildren && expanded && (
        <div>
          {folder.children.map((child) => (
            <TreeItem
              key={child.drive_id}
              folder={child}
              selectedId={selectedId}
              onSelect={onSelect}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Main FolderTreeSelect component
 */
export default function FolderTreeSelect({
  folders = [],
  value = null,
  onChange,
  loading = false,
  placeholder = 'Chon thu muc...',
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedFolder, setSelectedFolder] = useState(null)
  const containerRef = useRef(null)

  // Find selected folder from tree
  useEffect(() => {
    if (value && folders.length > 0) {
      const findFolder = (items, targetId) => {
        for (const item of items) {
          if (item.drive_id === targetId) return item
          if (item.children) {
            const found = findFolder(item.children, targetId)
            if (found) return found
          }
        }
        return null
      }
      setSelectedFolder(findFolder(folders, value))
    } else {
      setSelectedFolder(null)
    }
  }, [value, folders])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (folder) => {
    setSelectedFolder(folder)
    onChange?.(folder.drive_id, folder)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {/* Selected value / trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={clsx(
          'w-full flex items-center gap-2 px-3 py-2.5 border rounded-lg text-left transition-colors',
          isOpen
            ? 'border-primary-500 ring-2 ring-primary-100'
            : 'border-gray-300 hover:border-gray-400',
          loading && 'opacity-50 cursor-not-allowed'
        )}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
            <span className="text-gray-500">Dang tai...</span>
          </>
        ) : selectedFolder ? (
          <>
            <HiOutlineFolder className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <span className="truncate text-gray-900">{selectedFolder.name}</span>
            {selectedFolder.path && (
              <span className="text-xs text-gray-400 truncate ml-auto">
                {selectedFolder.path}
              </span>
            )}
          </>
        ) : (
          <>
            <HiOutlineFolder className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <span className="text-gray-500">{placeholder}</span>
          </>
        )}

        {/* Dropdown arrow */}
        <HiOutlineChevronDown
          className={clsx(
            'w-5 h-5 text-gray-400 ml-auto flex-shrink-0 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {folders.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <HiOutlineFolder className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Chua co folder nao.</p>
              <p className="text-xs mt-1">Hay sync toan bo truoc.</p>
            </div>
          ) : (
            <div className="py-1">
              {folders.map((folder) => (
                <TreeItem
                  key={folder.drive_id}
                  folder={folder}
                  selectedId={value}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
